import { useCallback, useEffect, useMemo } from 'react';
import { useRouter } from '@/hooks';
import { useAccountsQuery } from '@/hooks/queries/wallet/walletQueries';
import { createQueryString, getQueryParams } from '@/utils/fp/route/queryParams';
import { IAccount } from '@/types';
import { useAccountStore } from '@/store/accountStore';
import { useCurrentPath } from '@/hooks/general/useCurrentPath/useCurrentPath';

export const useAccounts = (onTabChange?: () => void) => {
    const { accounts, isLoading, error } = useAccountsQuery();
    const defaultAccount = accounts?.[0] || undefined;
    const defaultAccountId = defaultAccount ? defaultAccount.accountId : undefined;

    const router = useRouter();
    const { pathname, searchParams } = useCurrentPath();

    const queryParams = getQueryParams(searchParams)('accountId');

    const accountIdFromQueryParams = queryParams.accountId
        ? Number(queryParams.accountId)
        : undefined;

    const { updateSummary } = useAccountStore((state) => ({
        updateSummary: state.updateSummary,
    }));

    useEffect(() => {
        if (defaultAccount) {
            updateSummary({
                walletBalance: defaultAccount?.balance,
            });
        }
    }, [defaultAccount, updateSummary]);

    const updateUrlWithAccountId = (accountId: number) => {
        router.push(
            pathname +
                '?' +
                createQueryString({ name: 'accountId', value: accountId }, searchParams),
        );
    };

    const findAccountById = useCallback(
        (id: number | undefined): IAccount | undefined =>
            accounts?.find((account) =>
                id ? account.accountId === id : account.accountId === defaultAccountId,
            ) ?? undefined,
        [accounts, defaultAccountId],
    );

    const selectedAccount = useMemo(
        () => findAccountById(accountIdFromQueryParams),
        [accountIdFromQueryParams, findAccountById],
    );

    const handleChangeAccountTab = (accountId: number) => {
        updateUrlWithAccountId(accountId);
        onTabChange?.();
    };

    return {
        selectedAccount,
        accounts,
        handleChangeAccountTab,
        isLoading,
        error,
    };
};
